import {AsyncPipe, NgClass, NgComponentOutlet} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router, RouterLink} from '@angular/router';
import {take, tap} from 'rxjs';
import {PolicyGroupList} from 'src/app/prudent-plus-shared/models/policy-group';
import {Configuration} from 'src/app/prudent-plus-shared/services/configuration/configuration';
import {UserConfigurationService} from 'src/app/prudent-plus-shared/services/user-configuration/user-configuration.service';
import {AUTHENTICATION_SERVICE,
  AuthenticationService} from 'src/app/shared/services/authentication/authentication.service';
import {environment} from 'src/environments/environment';
import {DISPLAYABLE_DATE_FORMAT} from '../../constants/date';
import {PolicyGroupRenewal} from '../../constants/policy-group-renewal';
import {BreakpointService} from '../../services/breakpoint/breakpoint.service';
import {FeatureFlagsService} from '../../services/feature-flag/feature-flags.service';
import {Theme, ThemeHelpers, ThemeService} from '../../services/theme/theme.service';
import {DialogConfiguration} from '../dialog-configuration';
import {PrudentButtonComponent} from '../prudent-button/prudent-button.component';
import {SidebarService} from '../sidebar/sidebar.service';
import {DownloadDialogComponent} from './download-dialog/download-dialog.component';
import {NAV_BAR_SERVICE, NavBarService} from './nav-bar.service';
import {NAV_BAR_MENU_ITEM_TOKEN, NavbarMenuItem} from './navbar-menu-item';
import {PreviousYearPolicyDialogComponent} from './previous-year-policy-dialog/previous-year-poilcy-dialog.component';
import {NgClickOutsideDirective} from 'ng-click-outside2';
import {InstadeskDialogComponent} from './instadesk-dialog/instadesk-dialog.component';
import {EmployeeService} from 'src/app/prudent-plus-shared/services/employee/employee.service';
import {WalletHistory} from 'src/app/px/enrolment/policy-group-overview/employee-overview/wallet-history/models/wallet';
import {CurrencyCode, Money} from 'src/app/prudent-plus-shared/models/money';
import {EnvironmentService} from '../../services/environment/environment.service';
import {PrudentIconButtonComponent} from '../prudent-icon-button/prudent-icon-button.component';
const small = DialogConfiguration.small;
const medium = DialogConfiguration.medium;

import {PrudentExpandableButtonComponent} from '../prudent-expandable-button/prudent-expandable-button.component';

@Component({
  selector: 'app-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
  imports: [
    AsyncPipe,
    NgClass,
    PrudentButtonComponent,
    RouterLink,
    NgClickOutsideDirective,
    NgComponentOutlet,
    PrudentIconButtonComponent,
    PrudentExpandableButtonComponent,
  ],
})
export class NavBarComponent implements OnInit {
  protected readonly authenticationService = inject<AuthenticationService>(AUTHENTICATION_SERVICE);
  protected readonly navbarService = inject<NavBarService>(NAV_BAR_SERVICE);
  protected readonly items = inject<NavbarMenuItem[]>(NAV_BAR_MENU_ITEM_TOKEN, {
    optional: true,
  });
  protected readonly breakpointService = inject(BreakpointService);
  protected readonly featureFlagsService = inject(FeatureFlagsService);
  protected readonly themeService = inject(ThemeService);
  protected readonly Theme = Theme;
  protected readonly ThemeHelpers = ThemeHelpers;
  protected readonly configurationService = inject(UserConfigurationService);
  protected readonly DISPLAYABLE_DATE_FORMAT = DISPLAYABLE_DATE_FORMAT;
  protected readonly allowExplicitLogout = environment.navBarConfig.allowExplicitLogout;

  private readonly sidebarService = inject(SidebarService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  protected readonly environmentService = inject(EnvironmentService);
  protected readonly employee = inject(EmployeeService).currentEmployee;

  ngOnInit() {
    this.router.events.subscribe(() => {
      if (this.isSidebarOpen) {
        this.toggleSidebar();
      }
    });
  }

  whatsappConfig$ = this.navbarService.whatsappConfiguration$;
  walletHistory$ = this.navbarService.walletHistory$;
  isSidebarOpen: boolean = false;

  get configuration(): Configuration | undefined {
    return this.configurationService.configuration;
  }

  openDownloadDialog() {
    this.dialog.open(DownloadDialogComponent, small());
  }

  openInstaDeskDialog(whatsappNumber: string, isMobile: boolean) {
    if (isMobile) {
      const link = `https://wa.me/${whatsappNumber}`;

      window.location.href = `http://openbrowser?url=${encodeURIComponent(link)}`;
    } else {
      this.dialog.open(InstadeskDialogComponent, medium());
    }
  }

  public policyGroupId: string | null;
  allPolicies$ = this.navbarService.allPolicies$.pipe(
    tap((res: PolicyGroupList[] | null) => {
      if (res && res.length > 1) {
        this.policyGroupId = sessionStorage.getItem
        (PolicyGroupRenewal.SESSION_STORAGE_CURRENT_POLICY_KEY) ?
          sessionStorage.getItem(PolicyGroupRenewal.SESSION_STORAGE_CURRENT_POLICY_KEY) :
          res[0].policyGroupId;
      }
    })
  );

  showPrivacyPolicy() {
    const fallback = 'https://ppexstg01.prudentplus.in/static-files/privacy-policy.html';
    const source$ = this.navbarService.privatePolicyUrl$;

    if (!source$) {
      window.open(fallback, '_blank', 'noopener,noreferrer');

      return;
    }

    source$.pipe(take(1)).subscribe({
      next: (url) => {
        window.open(url || fallback, '_blank', 'noopener,noreferrer');
      },
      error: () => {
        window.open(fallback, '_blank', 'noopener,noreferrer');
      },
    });
  }

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
    this.sidebarService.forceExpand = this.isSidebarOpen;
  }

  viewPreviousYearPolicy(allPolicies: PolicyGroupList[]) {
    this.dialog.open(PreviousYearPolicyDialogComponent, small({
      allPolicies: allPolicies,
      policyGroupId: this.policyGroupId,
    })).afterClosed()
      .subscribe((policyGroupId: string) => {
        if (policyGroupId) {
          this.policyGroupId = policyGroupId;
          sessionStorage.setItem(PolicyGroupRenewal.SESSION_STORAGE_CURRENT_POLICY_KEY, policyGroupId);
          this.navbarService.reloadData();
        }
      });
  }

  // for current Prudent Plus Setup, if "show the last year policy" is true then show the last year policy tab or if we have session storage key then we are not checking the previous year configuration for viewLastYearPolicy
  get canSeeLastYearPolicy() {
    return this.configuration?.sidebar.viewLastYearPolicy || sessionStorage.getItem(PolicyGroupRenewal.SESSION_STORAGE_SEE_ALL_POLICIES) === 'true';
  }

  protected menu = false;

  toggleMenu() {
    this.menu = !this.menu;
  }

  hideMenu() {
    this.menu = false;
  }

  // [Need To implement the logic in second phase]
  openWalletDialog() {

  }

  constructor() {
  }

  getWalletTitle(history: WalletHistory[], isMobile: boolean = false) {
    const balanceWallet = new Money(
      history[history.length - 1].balanceWallet, CurrencyCode.INR
    ).description(this.employee);

    if (isMobile) {
      return `${balanceWallet}`;
    }

    return `Available Wallet : ${balanceWallet}`;
  }
}
